"use client";

import { forwardRef } from "react";
import PropTypes from "prop-types";
import { Autocomplete, TextField, Typography, Chip } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import clsx from "clsx";

const MDAutocomplete = forwardRef(
  (
    {
      labelType,
      labelText,
      options = [],
      value = [],
      placeholder = "",
      className = "",
      chipClasses = "",
      onChange,
      onInputChange,
      onMenuScrollToBottom,
      onFocus,
      autoComplete = true,
      ...rest
    },
    ref
  ) => {
    const shouldRenderCustomLabel = labelType === "non-sticky" && labelText;

    return (
      <div style={{ position: "relative" }}>
        {shouldRenderCustomLabel && (
          <Typography
            variant="caption"
            component="span"
            className="label"
            style={{
              marginBottom: 4,
              display: "block",
            }}
          >
            {labelText}
          </Typography>
        )}

        <Autocomplete
          {...rest}
          ref={ref}
          multiple
          options={options}
          value={value}
          onChange={(event, newValue, reason, details) => {
            if (onChange) {
              onChange(newValue);
            }
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (onInputChange) {
              onInputChange(newInputValue, { action: reason });
            }
          }}
          className={className}
          getOptionLabel={(option) => {
            return (
              option?.label ||
              option?.title ||
              option?.name ||
              String(option?.value || "")
            );
          }}
          isOptionEqualToValue={(option, value) => {
            if (!option || !value) return false;
            const optionId = option.value || option.id;
            const valueId = value.value || value.id;
            return String(optionId) === String(valueId);
          }}
          popupIcon={
            <ExpandLessIcon
              fontSize="small"
              style={{
                color: "#a2abb9",
                transform: "rotate(180deg)",
                transition: "transform 250ms ease",
              }}
            />
          }
          renderInput={(params) => (
            <form autoComplete={autoComplete ? "on" : "off"}>
              <TextField
                {...params}
                placeholder={placeholder}
                variant="outlined"
                size="small"
                fullWidth
                name={`hidden-autofill-fix-autocomplete${
                  className ? "-" + className : ""
                }`}
                autoComplete="off"
                // onFocus={(event) => {
                //   if (onFocus) {
                //     console.log("🎯 MDAutocomplete focused");
                //     onFocus(event);
                //   }
                //   if (params.InputProps?.onFocus) {
                //     params.InputProps.onFocus(event);
                //   }
                // }}
                onFocus={(event) => {
                  if (onFocus) {
                    // console.log("🎯 MDAutocomplete focused");
                    onFocus(event);
                  }
                  if (params.InputProps?.onFocus) {
                    params.InputProps.onFocus(event);
                  }
                }}
              />
            </form>
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const tagProps = getTagProps({ index });
              const label =
                option?.label ||
                option?.title ||
                option?.name ||
                String(option?.value || "");

              // ✅ FIX: Properly handle className without conflict
              const {
                className: originalClassName,
                onDelete,
                ...restTagProps
              } = tagProps;

              // console.log("🏷️ Chip classes debug:", {
              //   chipClasses,
              //   originalClassName,
              //   finalClassName: clsx(chipClasses, originalClassName),
              // });

              return (
                <Chip
                  {...restTagProps} // ✅ Spread first, then override specific props
                  variant="outlined"
                  label={label}
                  key={option?.value || option?.id || index}
                  size="small"
                  className={clsx(chipClasses, originalClassName)} // ✅ Combine classes properly
                  onDelete={onDelete} // ✅ Ensure onDelete works
                />
              );
            })
          }
          ListboxComponent={
            onMenuScrollToBottom
              ? forwardRef((props, listboxRef) => {
                  const { children, ...other } = props;

                  const handleScroll = (event) => {
                    const { target } = event;
                    const { scrollTop, scrollHeight, clientHeight } = target;

                    if (scrollHeight - scrollTop <= clientHeight + 50) {
                      if (onMenuScrollToBottom) {
                        // console.log("📜 Scrolled to bottom, loading more...");
                        onMenuScrollToBottom();
                      }
                    }
                  };

                  return (
                    <ul
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...other}
                      ref={listboxRef}
                      onScroll={handleScroll}
                      style={{
                        ...other.style,
                        maxHeight: "200px",
                        overflowY: "auto",
                      }}
                    >
                      {children}
                    </ul>
                  );
                })
              : undefined
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              minHeight: "40px",
              minWidth: "200px",
              "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d2d6da",
              },
              "& fieldset": {
                borderColor: "#d2d6da",
              },
              "&:hover fieldset": {
                borderColor: "#d1d5db",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#93c5fd",
                borderWidth: "2px",
              },
            },
            // ✅ CONSISTENT DROPDOWN ICON STYLING
            "& .MuiAutocomplete-popupIndicator": {
              color: "#a2abb9",
              padding: "4px",
              "&:hover": {
                backgroundColor: "transparent",
              },
            },
            // ✅ ROTATE ICON WHEN OPEN
            "&.Mui-focused .MuiAutocomplete-popupIndicator svg": {
              transform: "rotate(0deg)", // Open state
            },
            "& .MuiAutocomplete-popupIndicator svg": {
              transform: "rotate(180deg)", // Closed state
              transition: "transform 250ms ease",
            },
          }}
        />
      </div>
    );
  }
);

MDAutocomplete.displayName = "MDAutocomplete";

MDAutocomplete.propTypes = {
  labelType: PropTypes.string,
  labelText: PropTypes.string,
  options: PropTypes.array,
  value: PropTypes.array,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  chipClasses: PropTypes.string,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  onMenuScrollToBottom: PropTypes.func,
  onFocus: PropTypes.func,
  autoComplete: PropTypes.bool,
};

MDAutocomplete.defaultProps = {
  options: [],
  value: [],
  placeholder: "",
  className: "",
  chipClasses: "",
};

export default MDAutocomplete;
